import * as PIXI from "pixi.js";

import { TGameAssetAlias } from "@src/game/screens/GameScreen";
import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import { Assets } from "@src/core/assets/Assets";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";

export class FreespinOutroView implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _outroSprite!: PIXI.Sprite;

  private static readonly BASE_TEXT_STYLE = {
    fontFamily: "gameFont",
    fill: 0xffbb1e,
    fontWeight: "bold",
    wordWrap: false,
    letterSpacing: 3,
    align: "center",
    stroke: {
      color: 0x000033,
      width: 8,
      join: "round",
    },
  } as const;

  private _introWelcomeText!: PIXI.Text;
  private _introWinningsText!: PIXI.Text;
  private _introWelcomeTextStyleLandscape: PIXI.TextStyle = new PIXI.TextStyle({
    ...FreespinOutroView.BASE_TEXT_STYLE,
    fontSize: 35,
  });
  private _introWelcomeTextStylePortrait: PIXI.TextStyle = new PIXI.TextStyle({
    ...FreespinOutroView.BASE_TEXT_STYLE,
    fontSize: 45,
  });

  private _introWinningsTextStyleLandscape: PIXI.TextStyle = new PIXI.TextStyle(
    {
      fontSize: 80,
      ...FreespinOutroView.BASE_TEXT_STYLE,
      stroke: {
        color: 0x000033,
        width: 16,
        join: "round",
      },
    },
  );
  private _introWinningsTextStylePortrait: PIXI.TextStyle = new PIXI.TextStyle({
    fontSize: 90,
    ...FreespinOutroView.BASE_TEXT_STYLE,
    stroke: {
      color: 0x000033,
      width: 12,
      join: "round",
    },
  });

  private _animationTween!: GSAPTween;
  private _backgroundGraphics!: PIXI.Graphics;

  private _hasRunShowAnimation: boolean = false;

  public constructor() {
    this._setupEventListeners();

    this._initializeAssets();
    this._parentAssets();

    this._initializeComponents();
    this._parentComponents();
  }

  public playShowAnimation(): void {
    Audio.play("sndDialogScreenMove");
    this._animationTween?.kill();
    this._view.y = -1200;
    this._view.visible = true;
    this._introWinningsText.text = gameEngine.formatter.format(gameEngine.gameActionData.totalWin);
    this._hasRunShowAnimation = true;

    gsap.to(this._view, {
      y: 0,
      duration: 0.5,
      ease: "back.out(1)",
      onComplete: () => { },
    });
  }

  public playHideAnimation(): void {
    if (!this._hasRunShowAnimation) {
      return;
    }

    Audio.play("sndDialogScreenMove");
    this._animationTween?.progress(1);
    this._animationTween?.kill();

    gsap.to(this._view, {
      y: -1200,
      duration: 0.5,
      ease: "back.in(1)",
      onComplete: () => {
        this._view.visible = false;
        EventSystem.invoke(constants.GameEvents.FREESPIN_OUTRO_CLOSED);
        this._hasRunShowAnimation = false;
      },
    });
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequested.bind(this),
    );
  }

  private _onLandscapeLayoutRequested(): void {
    this._outroSprite.scale.set(1);

    this._introWelcomeText.position.set(0, -150);
    this._introWinningsText.position.set(0, 155);

    this._introWelcomeText.style = this._introWelcomeTextStyleLandscape;
    this._introWinningsText.style = this._introWinningsTextStyleLandscape;
  }

  private _onPortraitLayoutRequested(): void {
    this._outroSprite.scale.set(1.25);

    this._introWelcomeText.position.set(0, -170);
    this._introWinningsText.position.set(0, 200);

    this._introWelcomeText.style = this._introWelcomeTextStylePortrait;
    this._introWinningsText.style = this._introWinningsTextStylePortrait;
  }

  private _initializeAssets(): void {
    const gameAtlasSpritesheet1 =
      Assets.getAsset<TGameAssetAlias>("atlasGame1");

    this._outroSprite = new PIXI.Sprite(
      gameAtlasSpritesheet1.textures["bonus_outro_popup.webp"],
    );
    this._outroSprite.anchor.set(0.5);
    // Fix the sprite horizontal offset.
    this._outroSprite.position.set(-10, 0);
  }

  private _parentAssets(): void {
    this._view.addChild(this._outroSprite);
  }

  private _initializeComponents(): void {
    this._introWelcomeText = new PIXI.Text({
      text: "CONGRATULATIONS\nYOU WON",
      style: this._introWelcomeTextStyleLandscape,
    });
    this._introWelcomeText.anchor.set(0.5);
    this._introWelcomeText.y = -170;

    this._introWinningsText = new PIXI.Text({
      text: gameEngine.formatter.format(0),
      style: this._introWinningsTextStyleLandscape,
    });
    this._introWinningsText.anchor.set(0.5);

    this._backgroundGraphics = new PIXI.Graphics();
    this._backgroundGraphics
      .rect(-2000, -2000, 4000, 4000)
      .fill({ color: 0x000000, alpha: 0.7 });
    this._backgroundGraphics.zIndex = -1;
  }

  private _parentComponents(): void {
    this._view.addChild(this._backgroundGraphics);
    this._view.addChild(this._introWelcomeText);
    this._view.addChild(this._introWinningsText);
  }
}
